import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ANIMATION_DURATION } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import { MediaType } from "@is/types";
import { sortProductMedia } from "@is/utils";

type Props = {
  medias: MediaType[];
};

const SCROLL_MARGIN = 50;
const SMALL_SCREEN_HEIGHT = 1200;

const useManageProductMediaSlider = ({ medias }: Props) => {
  const { updateViewState } = useContext(ViewStateContext).content;

  const containerRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);

  const timeoutRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.style.overflowY = "auto";
          containerRef.current.style.opacity = "1";
          containerRef.current.style.animationFillMode = "unset";
        }
      }, ANIMATION_DURATION);
    }
  }, []);

  const handleScroll = useCallback(() => {
    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;

    // the end of the scroll
    if (clientWidth + scrollLeft >= scrollWidth - SCROLL_MARGIN) {
      return;
    }

    clearTimeout(timeoutRef.current);

    setIsScrolling(true);

    timeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 100);
  }, []);

  const sortedMedias = useMemo(() => sortProductMedia(medias), [medias]);

  const isSmallScreen = useMemo(() => {
    return window.innerHeight <= SMALL_SCREEN_HEIGHT;
  }, [window.innerHeight]);

  const handleSetMedias = useCallback(
    (index: number) => {
      updateViewState({
        enlargedMedias: { initialIndex: index, medias: sortedMedias },
        showEnlargedModal: true,
      });
    },
    [sortedMedias, updateViewState]
  );
  const handleSearchAndFooterDrawersClose = useCallback(() => {
    updateViewState({ showSearchAndFooterDrawers: false });

    trackEvent({ eventType: TrackingEventTypes.SearchDrawerClose, data: {} });
  }, [updateViewState]);

  return {
    isSmallScreen,
    containerRef,
    isScrolling,
    handleScroll,
    sortedMedias,
    handleSetMedias,
    handleSearchAndFooterDrawersClose,
  };
};

export default useManageProductMediaSlider;
